/**
 * useActivityNavigator — store-driven navigation API.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decisions D1, D3.
 *
 * This is the public hook callers use to drive activity navigation.
 * It performs the two-step pattern documented in D1:
 *   1. mutate the activity store (truth)
 *   2. call navigate() to keep the URL in sync (mirror)
 *
 * Each navigate() call writes a state object containing { activityId,
 * entryId } so the renderer (ActivityHost — Phase 2B) can correlate
 * the URL to a specific entry, and so popstate sync can recognize
 * which entry the user popped to.
 */

import { useMemo } from "react";
import { useLocation, useNavigate, type NavigateOptions } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";
import { getActivityRouteRule, type ActivityRouteRule } from "./activityRoutes";
import { closeActivityAndNavigate, readNavMeta } from "./dispatchBack";
import type { OpenActivityArgs, PushEntryArgs } from "./types";

type ActivityOpenArgs = OpenActivityArgs & Pick<ActivityRouteRule, "parentKinds" | "parentActivity">;

/**
 * Public call signature for nav.open / nav.replaceActivity. url 만 필수.
 * 나머지 필드 (kind/presentation/appBarAction/fallbackPath/parent*) 는
 * resolveActivityArgs 가 activityRoutes rule 에서 자동 채움.
 */
export type OpenCallArgs = { url: string; state?: unknown } & Partial<
  Omit<ActivityOpenArgs, "url" | "state">
>;

export interface ActivityNavigator {
  /** Open a new SubActivity above the current top. Returns its id. */
  open: (args: OpenCallArgs) => string;
  /** Push an entry inside the current top activity. */
  push: (args: PushEntryArgs) => void;
  /** Replace only the top entry of the current top activity. */
  replaceEntry: (args: PushEntryArgs) => void;
  /** Replace the entire current top activity. Returns the new id. */
  replaceActivity: (args: OpenCallArgs) => string;
  /**
   * Close one SubActivity (default: top).
   * Navigates to the underlying activity's URL or to the closing
   * activity's fallbackPath.
   */
  close: (activityId?: string) => void;
  /** Close every activity/dialog and navigate to a main destination. */
  closeAllTo: (url: string, options?: { replace?: boolean; state?: unknown }) => void;
  /**
   * Close every activity/dialog, then open one activity from its route rule.
   * options.fallbackPath 로 열리는 액티비티의 fallbackPath 를 명시 지정 가능 — draw
   * 흐름의 origin 메인탭(/gacha 등)을 유지해야 할 때 사용.
   */
  closeAllAndOpenTo: (
    url: string,
    options?: { state?: unknown; fallbackPath?: string },
  ) => string;
  /**
   * Close every activity above the latest matching kind (keep that activity and
   * everything below it), then open one activity from its route rule on top.
   *
   * 결제 완료처럼 funnel (참여/결제/토스) 만 걷어내되 그 아래의 detail activity 는
   * 살려둬야 할 때 사용. detail 이 살아있으면 그 fallbackPath (가챠샵/쿠지샵 origin)
   * 가 보존되어 뒤로가기·"다시 뽑기" 가 올바른 샵으로 복귀한다.
   * 스택에 해당 kind 가 없으면 closeAllAndOpenTo 와 동일하게 전체를 닫는다.
   */
  closeAboveKindAndOpenTo: (
    kind: string,
    url: string,
    options?: { state?: unknown; fallbackPath?: string },
  ) => string;
  /** Close activities above the latest matching kind and navigate to its top URL. */
  closeToKind: (kind: string, fallbackUrl: string) => void;
  /**
   * 구매 funnel(참여/결제/토스 등 전이 단계) 을 통째로 걷어내고 그 아래 origin(상세)으로
   * 복귀한다. origin 의 kind 를 하드코딩하지 않으므로 쿠지/가챠 공통 (PURCHASE_FUNNEL_KINDS).
   * 스택에 origin 이 없으면(전부 funnel/빈 스택) fallbackUrl 로 replace.
   */
  closeFunnel: (
    funnelKinds: ReadonlySet<string>,
    fallbackUrl: string,
    options?: { state?: unknown },
  ) => void;
}

/**
 * Build a NavigateOptions.state with activity metadata embedded.
 * Caller-supplied state is shallow-merged on top.
 */
function withActivityMeta(
  activityId: string,
  entryId: string,
  callerState: unknown,
): NavigateOptions["state"] {
  const base = { __activityId: activityId, __entryId: entryId };
  if (callerState !== undefined && callerState !== null && typeof callerState === "object") {
    return { ...base, ...(callerState as Record<string, unknown>) };
  }
  return base;
}

/**
 * args 와 rule 을 병합. rule 은 canonical kind/parent 매핑을 제공하고,
 * args 가 명시한 fallbackPath/presentation/appBarAction 은 호출자가 우선.
 *
 * currentPathname 이 주어지면 rule 의 fallbackPath 계산에 사용 (e.g. prize 가
 * 어디서 열렸는지에 따라 닫힘 시 돌아갈 경로가 달라짐).
 */
function resolveActivityArgs(
  args: OpenCallArgs,
  currentPathname = "/",
): ActivityOpenArgs {
  const rule = getActivityRouteRule(args.url, currentPathname);
  if (!rule) {
    // rule 이 없으면 호출자가 모든 필드를 직접 제공했다고 가정. type cast 로 좁힘.
    return args as ActivityOpenArgs;
  }
  return {
    // rule 이 canonical 한 필드 (kind/parent 관계)
    kind: rule.kind,
    parentKinds: rule.parentKinds ?? args.parentKinds,
    parentActivity: rule.parentActivity ?? args.parentActivity,
    // 호출자가 명시한 값이 우선, 없으면 rule 의 default 사용
    url: args.url,
    presentation: args.presentation ?? rule.presentation,
    appBarAction: args.appBarAction ?? rule.appBarAction,
    fallbackPath: args.fallbackPath ?? rule.fallbackPath,
    state: args.state ?? rule.state,
  };
}

function resolveActivityUrl(
  url: string,
  state?: unknown,
  currentPathname = "/",
): ActivityOpenArgs {
  const rule = getActivityRouteRule(url, currentPathname);
  if (!rule) {
    throw new Error(`Activity route rule not found for ${url}`);
  }
  return {
    ...rule,
    state,
  };
}

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, "") || "/";
}

function getTopActivity() {
  return useActivityStore.getState().activities.slice(-1)[0];
}

function getTopEntry() {
  return getTopActivity()?.entries.slice(-1)[0];
}

function isTopEntryUrl(url: string): boolean {
  const topEntry = getTopEntry();
  return topEntry ? normalizeUrl(topEntry.url) === normalizeUrl(url) : false;
}

function parentMatches(args: ActivityOpenArgs): boolean {
  const parentKinds = args.parentKinds ?? [];
  if (parentKinds.length === 0) return true;

  const top = getTopActivity();
  return top ? parentKinds.includes(top.kind) : false;
}

function ensureParentActivity(args: ActivityOpenArgs): void {
  if (parentMatches(args)) return;
  if (!args.parentActivity) return;

  const parent = resolveActivityArgs(args.parentActivity);
  ensureParentActivity(parent);
  if (parentMatches(args)) return;
  if (isTopEntryUrl(parent.url)) return;

  const parentHasParent = (parent.parentKinds?.length ?? 0) > 0;

  if (parentHasParent && parentMatches(parent)) {
    useActivityStore.getState().openActivity(parent);
    return;
  }

  useActivityStore.getState().replaceActivity(parent);
}

export function useActivityNavigator(): ActivityNavigator {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo<ActivityNavigator>(
    () => ({
      open: (args) => {
        const resolvedArgs = resolveActivityArgs(args, location.pathname);
        if (isTopEntryUrl(resolvedArgs.url)) {
          return getTopActivity()?.id ?? "";
        }
        ensureParentActivity(resolvedArgs);
        if (isTopEntryUrl(resolvedArgs.url)) {
          return getTopActivity()?.id ?? "";
        }
        const id = useActivityStore.getState().openActivity(resolvedArgs);
        const top = getTopActivity();
        const entry = top?.entries.slice(-1)[0];
        const entryId = entry?.id ?? "";
        navigate(resolvedArgs.url, {
          state: withActivityMeta(id, entryId, resolvedArgs.state),
        });
        return id;
      },

      push: (args) => {
        if (isTopEntryUrl(args.url)) return;
        useActivityStore.getState().pushEntry(args);
        const top = getTopActivity();
        const entry = top?.entries.slice(-1)[0];
        if (!top || !entry) return; // no-op when no top activity
        navigate(args.url, {
          state: withActivityMeta(top.id, entry.id, args.state),
        });
      },

      replaceEntry: (args) => {
        if (isTopEntryUrl(args.url)) return;
        useActivityStore.getState().replaceEntry(args);
        const top = getTopActivity();
        const entry = top?.entries.slice(-1)[0];
        if (!top || !entry) return;
        navigate(args.url, {
          replace: true,
          state: withActivityMeta(top.id, entry.id, args.state),
        });
      },

      replaceActivity: (args) => {
        const resolvedArgs = resolveActivityArgs(args, location.pathname);
        if (isTopEntryUrl(resolvedArgs.url)) {
          return getTopActivity()?.id ?? "";
        }
        ensureParentActivity(resolvedArgs);
        if (isTopEntryUrl(resolvedArgs.url)) {
          return getTopActivity()?.id ?? "";
        }
        const id = useActivityStore.getState().replaceActivity(resolvedArgs);
        const top = getTopActivity();
        const entry = top?.entries.slice(-1)[0];
        const entryId = entry?.id ?? "";
        navigate(resolvedArgs.url, {
          replace: true,
          state: withActivityMeta(id, entryId, resolvedArgs.state),
        });
        return id;
      },

      close: (activityId) => {
        closeActivityAndNavigate(activityId, navigate, location.state);
      },

      closeAllTo: (url, options) => {
        useActivityStore.getState().closeAll();
        navigate(url, {
          replace: options?.replace ?? true,
          state: options?.state,
        });
      },

      closeAllAndOpenTo: (url, options) => {
        // closeAll 로 스택을 전부 닫으므로 "호출자 컨텍스트"가 사라진다. 따라서 열리는
        // 액티비티의 fallbackPath 는 호출 시점 location(예: /ticket/open · /draw/result 같은
        // transient 페이지)이 아니라 rule 의 기본값을 써야 한다. currentPathname="" 을 넘기면
        // fallbackFromIndependent 가 rule default 를 반환한다. (currentPathname 을 넘기면
        // 뒤로가기 시 이미 사라진 draw 페이지로 떨어졌다가 lastDraw=null → /shop 으로
        // 잘못 리다이렉트되는 버그. GiftDonePage 가 같은 이유로 수동 우회하던 케이스.)
        const args = resolveActivityUrl(url, options?.state, "");
        if (isTopEntryUrl(args.url)) {
          return getTopActivity()?.id ?? "";
        }
        useActivityStore.getState().closeAll();
        // historyIsolated: closeAll 로 스택을 비운 뒤 replace 로 여는 액티비티는 그 아래
        // 브라우저 history 가 store 와 1:1 대응하지 않으므로 close 시 history-pop 금지.
        // fallbackPath override: draw 흐름의 origin 메인탭(/gacha 등)을 유지해야 할 때.
        const id = useActivityStore.getState().openActivity({
          ...args,
          fallbackPath: options?.fallbackPath ?? args.fallbackPath,
          historyIsolated: true,
        });
        const top = getTopActivity();
        const entry = top?.entries.slice(-1)[0];
        navigate(args.url, {
          replace: true,
          state: withActivityMeta(id, entry?.id ?? "", args.state),
        });
        return id;
      },

      closeAboveKindAndOpenTo: (kind, url, options) => {
        const args = resolveActivityUrl(url, options?.state, location.pathname);
        const store = useActivityStore.getState();
        const hasKind = store.activities.some((activity) => activity.kind === kind);
        if (hasKind) {
          store.closeAboveKind(kind);
        } else {
          store.closeAll();
        }
        // historyIsolated: funnel teardown(replace) 위에 열리므로 close 시 history-pop 금지
        // → underlying(kuji/detail) URL 로 replace 복귀. (dispatchBack.closeActivityAndNavigate)
        // fallbackPath override: 베이스 메인탭은 origin(/gacha 등)으로 유지해야 한다. draw
        // rule 의 하드코딩 "/shop" 을 쓰면 Layout 이 베이스를 쿠지샵으로 추론한다.
        const id = useActivityStore.getState().openActivity({
          ...args,
          fallbackPath: options?.fallbackPath ?? args.fallbackPath,
          historyIsolated: true,
        });
        const top = getTopActivity();
        const entry = top?.entries.slice(-1)[0];
        navigate(args.url, {
          replace: true,
          state: withActivityMeta(id, entry?.id ?? "", args.state),
        });
        return id;
      },

      closeToKind: (kind, fallbackUrl) => {
        const target = [...useActivityStore.getState().activities]
          .reverse()
          .find((activity) => activity.kind === kind);
        const targetEntry = target?.entries.slice(-1)[0];
        if (!target || !targetEntry) {
          navigate(fallbackUrl, { replace: true });
          return;
        }
        useActivityStore.getState().closeAboveKind(kind);
        navigate(targetEntry.url, {
          replace: true,
          state: withActivityMeta(target.id, targetEntry.id, targetEntry.state),
        });
      },

      closeFunnel: (funnelKinds, fallbackUrl, options) => {
        const activities = useActivityStore.getState().activities;
        // top 부터 funnel 을 건너뛰어 origin(첫 비-funnel activity) 인덱스 탐색.
        let originIdx = activities.length - 1;
        while (originIdx >= 0 && funnelKinds.has(activities[originIdx].kind)) {
          originIdx -= 1;
        }
        const origin = originIdx >= 0 ? activities[originIdx] : null;
        const closed = activities.slice(originIdx + 1); // 닫을 funnel activity 들
        // origin 바로 위의 funnel 시작 activity — 그 fallbackPath 가 곧 funnel 이 열린 출처
        // (예: 쿠지 상세). origin 이 스택에 없을 때(상세가 베이스 route 인 경우) 복귀 경로를
        // 여기서 도출하므로 caller 가 경로를 하드코딩할 필요가 없다.
        const bottomFunnel = closed[0] ?? null;
        const topClosed = closed[closed.length - 1] ?? null;

        // (a) in-app 경로 — 현재 history 항목이 우리가 top funnel 을 위해 push 한 것이면
        //     funnel 이 push 한 entry 개수만큼 navigate(-N) 으로 history 를 pop 한다. 그래야
        //     teardown 후 상세에서 back 시 stale funnel entry(/shop/:id/quantity 등)로 떨어져
        //     상세가 다시 열리는 현상을 막는다. pop → popstate → usePopstateSync.reconcileFromUrl
        //     이 store 를 정리하므로 여기서 store 를 직접 mutate 하지 않는다 (close() 와 동일 패턴).
        const meta = readNavMeta(location.state);
        const popCount = closed.reduce((n, a) => n + a.entries.length, 0);
        const canPopHistory =
          popCount > 0 &&
          !!topClosed &&
          meta?.__activityId === topClosed.id &&
          !closed.some((a) => a.historyIsolated);

        if (canPopHistory) {
          navigate(-popCount);
          return;
        }

        // (b) deep-link / hydrated / vendor-iframe / isolated — pop 할 history 가 없으므로
        //     store mutate + replace 로 복귀.
        useActivityStore.getState().closeFunnel(funnelKinds);
        if (origin) {
          // origin 이 activity 로 살아있음 — 그 URL 로 복귀 (kind 무관, 제네릭).
          const entry = origin.entries.slice(-1)[0];
          navigate(entry?.url ?? fallbackUrl, {
            replace: true,
            state: entry
              ? withActivityMeta(origin.id, entry.id, entry.state)
              : options?.state,
          });
          return;
        }
        // origin activity 없음 — 상세는 그 아래 베이스 route 로 mount 되어 있다. 닫은 funnel 의
        // 최하단 activity 가 기억한 fallbackPath(= 열린 출처 = 상세)로 복귀. 활동 정보가 전혀
        // 없을 때(cold-start)만 caller fallbackUrl.
        navigate(bottomFunnel?.fallbackPath ?? fallbackUrl, {
          replace: true,
          state: options?.state,
        });
      },
    }),
    [navigate, location.pathname, location.state],
  );
}
